'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  EXCEL_MUTED,
  EXCEL_SHEET,
  EXCEL_WARN,
  MONTH_ACTIVITIES_COMPACT,
  MONTH_ACTIVITY_PCT,
  MONTH_PLANNING,
  MONTH_TOTALS,
  type ClientNr,
} from '../demo-data'
import { useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppSubHeader, BoardName, HeaderStat, PlanningPill } from './app-screen'
import styles from './excel-grid-visual.module.css'
import { useScenario } from './use-scenario'

type CellTone = 'plain' | 'muted' | 'warn' | 'late'
type ExcelCell = { text: string; tone: CellTone }

type RowData = {
  /** Cramped Excel-column name (col A) — deliberately sloppy, unlike the real client name. */
  name: string
  /** Canonical client this row is about; supplies the full name + legal form after the morph. */
  nr: ClientNr
  /** Cramped Excel bedrijfsvorm abbreviation (col B) — collapses away once flipped. */
  tag: string
  indent?: boolean
  excel: readonly ExcelCell[]
}

/** A cell's tone follows from what it says: the sheet's own vocabulary decides how it reads. */
const excelCell = (text: string): ExcelCell => ({
  text,
  tone: EXCEL_WARN.has(text) ? 'warn' : EXCEL_MUTED.has(text) ? 'muted' : 'plain',
})

const ROWS: readonly RowData[] = MONTH_PLANNING.map((row) => {
  const sheetRow = EXCEL_SHEET[row.nr]
  if (!sheetRow) throw new Error(`No Excel row authored for client ${row.nr}`)
  const { parent } = client(row.nr)
  return {
    name: sheetRow.name,
    nr: row.nr,
    tag: sheetRow.tag,
    indent: Boolean(parent),
    excel: sheetRow.cells.map(excelCell),
  }
})

/** The resolved Offsoo cells for a row, or null for the overflow client the card cuts off. */
function offsooRowOrNull(nr: ClientNr) {
  return MONTH_PLANNING.find((candidate) => candidate.nr === nr) ?? null
}

const CELL_STYLE: Record<CellTone, { background: string; color: string }> = {
  plain: { background: 'var(--xl-paper)', color: 'var(--xl-text)' },
  muted: { background: 'var(--xl-paper)', color: 'var(--xl-head-fg)' },
  warn: { background: 'var(--xl-warn)', color: 'var(--xl-warn-fg)' },
  late: { background: 'var(--xl-late)', color: 'var(--xl-late-fg)' },
}

// Same columns and percentages as the month board, so the two visuals stay 1:1. Cropped to three:
// each row's Excel side is hand-authored with three cells, and the morph is column-for-column.
const offsooColumns = (t: VisualCopy) =>
  MONTH_ACTIVITIES_COMPACT.map((activity) => ({
    label: t.monthActivity[activity.key].label.toUpperCase(),
    pct: MONTH_ACTIVITY_PCT[activity.key],
  }))

/*
 * A spreadsheet becoming a board, with one Offsoo counterpart for every Excel element rather than a
 * demolition and a replacement:
 *
 *   title bar `Planning2026-DEFINITIEF(3).xlsx`  ->  the Offsoo header, month and completion
 *   formula bar `TE LAAT — wie pakt dit op???`   ->  folds away; the header already carries progress
 *   column letters A B C D E                     ->  ADMINISTRATIE · OMZETBELASTING · SALARIS
 *   yellow `###` / `wie??` cells                 ->  status pills
 *   cramped `- Kanaalstede`                      ->  the client's full name and legal form
 *
 * The sheet tabs are the one exception, and they carry the heading: `Planning | 2025 | Q3 kopie??`
 * do not fade out, they slide together into a single board. Three tabbladen becoming one overzicht
 * is the argument this section makes, so it happens in the picture and not only in the words.
 *
 * The rows cross first and the chrome last, which is the opposite of what this visual used to do.
 * Stripping the chrome up front left several beats of a grid belonging to neither side; keeping the
 * window in place gives the eye something to hold while its contents change underneath.
 */
/**
 * Column proportions, gutter and gap shared by the sub-header grid and the (flex) body rows. Both
 * sides read these same numbers, which is what keeps the header locked to the table underneath.
 */
/* `minmax(0, …)`, not bare `fr`: a bare fraction refuses to go below its content's own width, and
   the status labels inside these cells do not wrap. On a phone that pushed the last column clean
   off the card. Both layers share this template, so they have to shrink together. */
const GRID_TEMPLATE = 'minmax(0, 1.9fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)'
const ROW_GAP = 8
const ROW_PAD_X = 12

/**
 * One more client than fits, cropped by the card's edge.
 *
 * @see SHEET_ROWS
 *
 * A list that ends exactly at the bottom of its box reads as the whole list; one that runs past it
 * reads as a view onto something larger — which is the point of a board holding 812 clients.
 */
const OVERFLOW_NR: ClientNr = '10014'

/**
 * The rows as drawn: the month's clients plus one more that the card cuts off.
 *
 * Both layers render this same list, so their rows are the same count and the same height and the
 * sweeping edge crosses them without anything stepping out of line.
 */
/** The client the cut-off bottom row shows — one further down the same board. */
const NEXT_NR: ClientNr = '10018'

const SHEET_ROWS: readonly RowData[] = [
  ...ROWS,
  {
    name: client(OVERFLOW_NR).short,
    nr: OVERFLOW_NR,
    tag: client(OVERFLOW_NR).form,
    indent: false,
    excel: [excelCell('ok'), excelCell('###'), excelCell('n.v.t.')],
  },
]

/**
 * The file becoming a board, at reading pace.
 *
 * Two beats and one movement: the sheet sits still long enough to be taken in, then the board sweeps
 * across it from left to right and stays. The sweep is deliberately slow — this is the section's
 * whole argument, and hurrying it turns the argument into a flicker.
 */
const BEAT_HOLDS = [
  2600, // 0  the file, whole and still — what the visual rests on
  6800, // 1  the sweep (4.8s) and then the finished board, held
] as const

const WIPE_AT = 1

const REST_BEAT = 0 // the Excel file: what the heading promises, and the frame most visitors judge

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive grid state;
 *  `null` renders the resting frame: the Excel file, whole, which is what the heading promises. */
function effectiveBeat(beat: number | null): number {
  return beat ?? REST_BEAT
}

/**
 * Two whole layers, one edge.
 *
 * The sheet and the board are each rendered complete, stacked exactly on top of each other, and the
 * board is revealed left to right by a single clip. Converting cell by cell — which is what this
 * did before — meant every cell crossed on its own schedule and the middle of the transition was a
 * mess of half-faded fragments. One edge travelling across two finished pictures is calmer to watch
 * and says the same thing.
 *
 * The two layers share a four-zone grid with fixed top, header and bottom bands, so their rows are
 * the same height and land on the same pixels. Without that the edge would show the board's rows
 * stepping out of line with the sheet's as it passed.
 */
export function ExcelGridVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)
  const revealed = effBeat >= WIPE_AT

  return (
    <AppUiVisual label={t.screen.excel.caption} className={className}>
      <div
        ref={ref}
        data-visual="excel"
        className={clsx(
          styles.root,
          revealed ? styles.shapeCard : styles.shapeSquare,
          'relative h-full w-full overflow-hidden border text-[8.5px] shadow-sm',
          'border-[color:var(--xl-frame)] bg-[var(--xl-paper)] text-[color:var(--xl-text)]',
          className,
        )}
      >
        {/* ---------------------------------------------------------------- the sheet, underneath */}
        <div className={styles.layer}>
          <div className="flex min-h-0 flex-col">
            <div
              className="flex flex-1 items-center justify-between gap-2 px-2"
              style={{ background: 'var(--xl-green)' }}
            >
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="flex size-[10px] shrink-0 items-center justify-center rounded-[2px] bg-white">
                  <span className="text-[7px] leading-none font-bold" style={{ color: 'var(--xl-green)' }}>
                    X
                  </span>
                </span>
                <span className="truncate text-[7.5px] font-medium whitespace-nowrap text-white">
                  Planning2026-DEFINITIEF(3).xlsx — Excel
                </span>
              </div>
              <span className="shrink-0 text-[8px] leading-none whitespace-nowrap text-white">─ ▢ ✕</span>
            </div>
            <div
              className="flex flex-1 items-center gap-1.5 border-b px-1.5"
              style={{ background: 'var(--xl-bar)', borderColor: 'var(--xl-line)' }}
            >
              <span
                className="shrink-0 rounded-[2px] border px-1 py-[1px] text-[7.5px] font-semibold whitespace-nowrap"
                style={{ background: 'var(--xl-paper)', borderColor: 'var(--xl-frame)', color: 'var(--xl-text)' }}
              >
                B4
              </span>
              <span className="shrink-0 text-[7px] italic" style={{ color: 'var(--xl-head-fg)', fontFamily: 'serif' }}>
                fx
              </span>
              <span
                className="min-w-0 flex-1 truncate text-[7px] whitespace-nowrap"
                style={{ color: 'var(--xl-text)' }}
              >
                {t.screen.excel.lateNote}
              </span>
            </div>
          </div>

          {/* Four columns, matching the board's four: the legal-form column the sheet used to carry
              is gone, so the two layers line up column for column and the eye has less to track. */}
          <div className="flex border-b" style={{ background: 'var(--xl-head-bg)', borderColor: 'var(--xl-line)' }}>
            <span className="w-[16px] shrink-0 border-r" style={{ borderColor: 'var(--xl-line)' }} />
            <span
              className="flex flex-[1.9] items-center justify-center border-r text-[8px] font-bold"
              style={{ color: 'var(--xl-head-fg)', borderColor: 'var(--xl-line)' }}
            >
              A
            </span>
            {['B', 'C', 'D'].map((col) => (
              <span
                key={col}
                className="flex flex-1 items-center justify-center border-r text-[8px] font-bold last:border-r-0"
                style={{ color: 'var(--xl-head-fg)', borderColor: 'var(--xl-line)' }}
              >
                {col}
              </span>
            ))}
          </div>

          <div className="flex min-h-0 flex-col">
            {SHEET_ROWS.map((row, ri) => (
              <div
                key={`${row.nr}-${ri}`}
                className="flex flex-1 items-stretch border-b last:border-b-0"
                style={{ borderColor: 'var(--xl-line)' }}
              >
                <span
                  className="flex w-[16px] shrink-0 items-center justify-center border-r text-[8px] font-semibold"
                  style={{ background: 'var(--xl-head-bg)', color: 'var(--xl-head-fg)', borderColor: 'var(--xl-line)' }}
                >
                  {ri + 1}
                </span>
                <span
                  className="flex flex-[1.9] items-center truncate border-r text-[10px] font-medium whitespace-nowrap"
                  style={{
                    color: 'var(--xl-text)',
                    borderColor: 'var(--xl-line)',
                    // A subsidiary is stepped in with real space rather than a typed "- ", which is
                    // how the board above draws the same relationship.
                    paddingLeft: row.indent ? 14 : 4,
                    paddingRight: 4,
                  }}
                >
                  {row.name}
                </span>
                {row.excel.map((cell, ci) => (
                  <span
                    key={ci}
                    className="flex flex-1 items-center truncate border-r px-1 text-[10px] whitespace-nowrap last:border-r-0"
                    style={{ ...CELL_STYLE[cell.tone], borderColor: 'var(--xl-line)' }}
                  >
                    {cell.text}
                  </span>
                ))}
              </div>
            ))}
          </div>

          <div
            className="flex items-center gap-0.5 border-t px-1"
            style={{ background: 'var(--xl-bar)', borderColor: 'var(--xl-line)' }}
          >
            <span
              className="rounded-t-[3px] border-t-2 px-1.5 py-1 text-[7.5px] font-semibold whitespace-nowrap"
              style={{ background: 'var(--xl-paper)', borderColor: 'var(--xl-green)', color: 'var(--xl-text)' }}
            >
              Planning
            </span>
            <span className="px-1.5 py-1 text-[7.5px] whitespace-nowrap" style={{ color: 'var(--xl-head-fg)' }}>
              2025
            </span>
            <span className="px-1.5 py-1 text-[7.5px] whitespace-nowrap" style={{ color: 'var(--xl-head-fg)' }}>
              {t.screen.excel.copyNote}
            </span>
            <span
              className="px-1.5 py-1 text-[7px] font-semibold whitespace-nowrap"
              style={{ color: 'var(--xl-head-fg)' }}
            >
              +
            </span>
          </div>
        </div>

        {/* The leading edge of the sweep — a bright rule with a soft glow, travelling with the
            reveal. Without it the board simply appears from the left; with it, something is clearly
            passing over the sheet and leaving the board behind. */}
        <span className={clsx(styles.edge, revealed && styles.edgeSwept)} aria-hidden="true" />

        {/* ------------------------------------------------- the board, revealed left to right */}
        <div className={clsx(styles.layer, styles.board, revealed && styles.boardRevealed)}>
          <AppHeader
            className="min-h-0 border-b-0"
            title={t.screen.monthPlanning.title}
            detail={t.period.month}
            right={<HeaderStat value={`${MONTH_TOTALS.donePct}%`} label={t.word.complete} />}
          />

          <AppSubHeader
            className="min-h-0 border-b-0"
            style={{ gridTemplateColumns: GRID_TEMPLATE, gap: ROW_GAP, paddingInline: ROW_PAD_X }}
            rowLabel={t.word.client}
            columns={offsooColumns(t).map((col) => ({
              label: (
                <>
                  {col.label} <b className={styles.headPct}>{col.pct}%</b>
                </>
              ),
              pct: col.pct,
            }))}
          />

          <div className="flex min-h-0 flex-col" style={{ paddingInline: ROW_PAD_X }}>
            {SHEET_ROWS.map((row, ri) => {
              const planning = offsooRowOrNull(row.nr)
              return (
                <div
                  key={`${row.nr}-${ri}`}
                  className="grid flex-1 items-center"
                  style={{ gridTemplateColumns: GRID_TEMPLATE, gap: ROW_GAP }}
                >
                  <div className="flex min-w-0 items-center">
                    {/* No legal-form tag: five of them down a narrow column competed with the names
                        they belong to, and the name is what the row is about. */}
                    <BoardName nr={row.nr} indent={row.indent} showTag={false} />
                  </div>
                  {row.excel.map((_, ci) => (
                    <div key={ci} className="flex min-w-0 items-center">
                      <PlanningPill tone={planning ? planning.cells[ci] : 'gepland'} className={styles.boardPill} />
                    </div>
                  ))}
                </div>
              )
            })}
          </div>

          {/* The next client, cut off by the card's edge. It balances the sheet's tab strip so both
              layers put their rows on the same pixels, and it does something better than balance:
              a list that stops exactly at the bottom of its box reads as the whole list, and this
              one is a window onto 812 clients. */}
          <div
            className={styles.overflowRow}
            style={{ paddingInline: ROW_PAD_X, gridTemplateColumns: GRID_TEMPLATE, gap: ROW_GAP }}
          >
            <div className="flex min-w-0 items-center">
              <BoardName nr={NEXT_NR} showTag={false} />
            </div>
            {[0, 1, 2].map((ci) => (
              <div key={ci} className="flex min-w-0 items-center">
                <PlanningPill tone={ci === 0 ? 'loopt' : 'gepland'} className={styles.boardPill} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppUiVisual>
  )
}
